import React, { useState, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { navigationClickEvent } from 'analytics/events/studio';
import { SanityKeyed, TabbedCollection } from 'types/studio/studio';
import TabbedCollectionSlide from '../TabbedCollectionSlide/TabbedCollectionSlide';
import TabbedCollectionThumb from '../TabbedCollectionThumb/TabbedCollectionThumb';
import styles from './TabbedCollectionContent.module.scss';

type Props = {
  items: SanityKeyed<TabbedCollection['slides']>;
  sectionType: string;
  headline?: string;
  visibleOnPageLoad: boolean;
} & Pick<TabbedCollection, 'isAGZVariant' | 'theme'>;

const TabbedCollectionContent = ({
  items,
  sectionType,
  isAGZVariant,
  headline,
  theme,
  visibleOnPageLoad
}: Props) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const thumbsWrap = useRef<HTMLDivElement>(null!);
  const thumbsContainer = useRef<HTMLDivElement>(null!);

  const scrollToThumb = (position?: number) => {
    thumbsWrap.current.scroll({
      left: position,
      behavior: 'smooth'
    });
  };

  const handleThumbSelect = useCallback(
    (index: number, title: string) => {
      setActiveSlide(index);

      // scroll to thumbs
      const headerHeight = document.querySelector('header')?.clientHeight;
      const thumbsWrapOffset = thumbsContainer.current.offsetTop;
      let finalOffset = thumbsWrapOffset;

      if (headerHeight) {
        if (window.matchMedia('(min-width: 768px)').matches) {
          finalOffset = thumbsWrapOffset - headerHeight - 16;
        } else {
          finalOffset = thumbsWrapOffset - 16;
        }
      }

      window.scrollTo({
        top: finalOffset,
        behavior: 'smooth'
      });

      navigationClickEvent({
        location: sectionType,
        navigationType: 'accordion',
        header: headline,
        clickText: title
      });
    },
    [headline, sectionType]
  );

  const containerRef = useRef<HTMLDivElement>(null!);

  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStartSlides = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMoveSlides = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEndSlides = useCallback(() => {
    let syncThumb = 1;
    let thumbsSyncPosition = 0;
    const itemWidth = thumbsContainer.current.scrollWidth / items.length;
    // next slide
    if (touchStart - touchEnd > 50) {
      if (activeSlide < items.length - 1) {
        setActiveSlide(activeSlide + 1);
        syncThumb = activeSlide + 1;
      }

      if (activeSlide >= items.length - 2) {
        thumbsSyncPosition =
          thumbsContainer.current.scrollWidth -
          thumbsWrap.current.offsetWidth +
          parseInt(getComputedStyle(thumbsWrap.current).paddingRight, 10) * (items.length - 1);
      } else {
        thumbsSyncPosition =
          itemWidth * syncThumb -
          itemWidth / 2 -
          parseInt(getComputedStyle(thumbsContainer.current).gap, 10) * (items.length - 1);
      }
      scrollToThumb(thumbsSyncPosition);
    }
    // prev slide
    if (touchStart - touchEnd < -50 && activeSlide > 0) {
      setActiveSlide(activeSlide - 1);
      syncThumb = activeSlide - 1;

      if (activeSlide === 0) {
        thumbsSyncPosition = 0;
      } else {
        thumbsSyncPosition =
          itemWidth * syncThumb -
          itemWidth / 2 -
          parseInt(getComputedStyle(thumbsContainer.current).gap, 10) * 2;
      }
      scrollToThumb(thumbsSyncPosition);
    }
  }, [activeSlide, items.length, touchEnd, touchStart]);

  return (
    <div className={styles.container}>
      <div className={styles.thumbsWrap} ref={thumbsWrap}>
        <div ref={thumbsContainer} className={styles.thumbsContainer}>
          {items.map((item, index) => {
            const { _key: key } = item;
            return (
              <TabbedCollectionThumb
                key={key}
                onClick={() => handleThumbSelect(index, item.thumbTitle)}
                selected={index === activeSlide}
                index={index}
                isAGZVariant={isAGZVariant}
                theme={theme}
                {...item}
              />
            );
          })}
        </div>
      </div>
      <div className={clsx(styles.mainWrap)}>
        <div
          ref={containerRef}
          className={styles.mainContainer}
          onTouchStart={handleTouchStartSlides}
          onTouchMove={handleTouchMoveSlides}
          onTouchEnd={handleTouchEndSlides}>
          {items.map((item, index) => {
            const { _key: key } = item;
            return (
              <TabbedCollectionSlide
                key={key}
                selected={index === activeSlide}
                {...item}
                isAGZVariant={isAGZVariant}
                theme={theme}
                // Only the first slide will be showing on initial load.
                visibleOnPageLoad={visibleOnPageLoad && index === 0}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TabbedCollectionContent;
